import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { AutomationStepExecutionStatus } from '../../enums';
import { AutomationExecutionEntity } from './automation-execution.entity';
import { AutomationStepEntity } from './automation-step.entity';
import { BaseEntity } from './base.entity';

@Entity('_automation_step_execution')
export class AutomationStepExecutionEntity extends BaseEntity {
	@Column({
		name: '_fk__automation_execution__id',
		type: 'int',
		unsigned: true,
	})
	public executionId!: number;

	@Column({
		name: '_fk__automation_step__id',
		type: 'int',
		unsigned: true,
	})
	public stepId!: number;

	@Column({
		name: '_execution_status',
		type: 'enum',
		enum: AutomationStepExecutionStatus,
		default: `'${AutomationStepExecutionStatus.PENDING}'`,
	})
	public executionStatus!: AutomationStepExecutionStatus;

	@Column({ name: '_started_at', type: 'timestamp', nullable: true })
	public startedAt?: Date;

	@Column({ name: '_completed_at', type: 'timestamp', nullable: true })
	public completedAt?: Date;

	@Column({ name: '_scheduled_for', type: 'timestamp', nullable: true })
	public scheduledFor?: Date;

	@Column({ name: '_execution_data', type: 'json', nullable: true })
	public executionData?: Record<string, any>;

	@Column({ name: '_result_data', type: 'json', nullable: true })
	public resultData?: Record<string, any>;

	@Column({ name: '_error_message', type: 'text', nullable: true })
	public errorMessage?: string;

	@Column({ name: '_retry_count', type: 'int', default: 0 })
	public retryCount!: number;

	@Column({
		name: '_last_check_at',
		type: 'timestamp',
		nullable: true,
		comment: 'Last time this step was checked/processed while waiting',
	})
	public lastCheckAt?: Date;

	@Column({
		name: '_waiting_event_type',
		type: 'varchar',
		length: 100,
		nullable: true,
		comment: 'Event type this step is waiting for',
	})
	public waitingEventType?: string;

	@Column({
		name: '_waiting_constraint',
		type: 'varchar',
		length: 255,
		nullable: true,
		comment:
			'Constraint for event matching (e.g., entity ID, custom identifier)',
	})
	public waitingConstraint?: string;

	@Column({
		name: '_fk__automation_step_execution__id',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment: 'Parent step execution ID for parallel child executions',
	})
	public parentStepExecutionId?: number;

	@Column({
		name: '_fk__automation_step_execution__id-previous',
		type: 'int',
		unsigned: true,
		nullable: true,
		comment:
			'Previous step execution ID for tracking sequential flow within branches',
	})
	public previousStepExecutionId?: number;

	@Column({
		name: '_needs_subtask_finally',
		type: 'boolean',
		nullable: true,
		comment:
			'When true, system will call handleSubtaskFinally when all subtasks complete',
	})
	public needsSubtaskFinally?: boolean;

	@Column({
		name: '_finally_subtask_created',
		type: 'boolean',
		nullable: true,
		comment:
			'Atomic flag to prevent race condition - set to true when finally subtask is created',
	})
	public finallySubtaskCreated?: boolean;

	@ManyToOne(
		() => AutomationExecutionEntity,
		(execution) => execution.stepExecutions,
	)
	@JoinColumn({ name: '_fk__automation_execution__id' })
	public execution!: AutomationExecutionEntity;

	@ManyToOne(() => AutomationStepEntity, (step) => step.executions)
	@JoinColumn({ name: '_fk__automation_step__id' })
	public step!: AutomationStepEntity;

	@ManyToOne(() => AutomationStepExecutionEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_step_execution__id' })
	public parentStepExecution?: AutomationStepExecutionEntity;

	@ManyToOne(() => AutomationStepExecutionEntity, { nullable: true })
	@JoinColumn({ name: '_fk__automation_step_execution__id-previous' })
	public previousStepExecution?: AutomationStepExecutionEntity;

	@Index('idx_automation_step_execution_execution_step')
	public static executionStepIndex: [string, string] = [
		'executionId',
		'stepId',
	];

	@Index('idx_automation_step_execution_parent')
	public static parentStepIndex: [string] = ['parentStepExecutionId'];

	@Index('idx_automation_step_execution_previous')
	public static previousStepIndex: [string] = ['previousStepExecutionId'];

	@Index('idx_automation_step_execution_status')
	public static statusIndex: [string] = ['executionStatus'];

	@Index('idx_automation_step_execution_scheduled')
	public static scheduledIndex: [string] = ['scheduledFor'];

	@Index('idx_waiting_event')
	public static waitingEventIndex: [string, string, string] = [
		'executionStatus',
		'waitingEventType',
		'waitingConstraint',
	];
}
